import { Stack } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { first } from 'lodash';
import { useState } from 'react';

import { SmallTableEmptyState } from '@/components/display/SmallTableEmptyState/SmallTableEmptyState';
import { Button } from '@/components/form/baseInputs/Button';
import { BankIcon } from '@/components/icons/BankIcon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Loader } from '@/components/progress/Loader/Loader';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { DataTableCard } from '@/components/tables/DataTable/DataTableCard';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { AddHoldingsFormModal } from '@/modules/estate/components/AddHoldingsFormModal/AddHoldingsFormModal';
import { EstateBreadcrumb } from '@/modules/estate/components/EstateBreadCrumb';
import { useEstateHoldingsContext } from '@/modules/estate/contexts/estateHoldings.context';
import { EditPolicySideDrawer } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupCard/DirectHoldingsGroupSection/HoldingsGroupPolicies/EditPolicySideDrawer';
import { useEstateLifeInsurancePoliciesListQuery } from '@/modules/estate/EstateLifeInsurances/graphql/EstateLifeInsurances.generated';
import {
  LifeInsurancePolicyRowData,
  mapLifeInsurancePolicyRows,
  useMapLifeInsurancePolicyColumns,
} from '@/modules/estate/EstateLifeInsurances/hooks/useMapLifeInsurancePoliciesToRowsAndColumns';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { getNodes } from '@/utils/graphqlUtils';

export function EstateLifeInsurances() {
  const canEdit = useCanEditHouseholdData();
  const [showAddHoldingModal, setShowAddHoldingModal] = useState(false);
  const [editingPolicyRow, setEditingPolicyRow] =
    useState<LifeInsurancePolicyRowData | null>(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);

  const { householdId, primaryClients } = useHouseholdDetailsContext();
  const { entities, loading: contextLoading } = useEstateHoldingsContext();
  const entityIDs = (entities ?? []).map((e) => e.id);

  const { data, loading, refetch } = useEstateLifeInsurancePoliciesListQuery({
    variables: {
      entityIDs: entityIDs,
      householdID: householdId!,
    },
    skip: !householdId,
  });

  const columns = useMapLifeInsurancePolicyColumns({ refetch });
  const rows = mapLifeInsurancePolicyRows(
    getNodes(data?.entities),
    first(primaryClients)?.id || ''
  );

  const handleRowClick = ({
    row,
  }: GridRowParams<LifeInsurancePolicyRowData>) => {
    setEditingPolicyRow(row);
    setEditDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setEditingPolicyRow(null);
    setEditDrawerOpen(false);
  };

  if (contextLoading || loading) {
    return (
      <Loader
        boxProps={{
          alignItems: 'center',
          position: 'absolute',
          left: 8,
          top: 13,
        }}
        circularProgressProps={{ size: 15 }}
      />
    );
  }

  if (!loading && rows.length === 0) {
    return (
      <>
        <SmallTableEmptyState
          fill="viewport"
          icon={<BankIcon size={32} color={COLORS.TEXT.PRIMARY} />}
          heading="No life insurance policies"
          description="There are no life insurance policies associated with this client"
          action={
            canEdit ? (
              <Button
                variant="primary"
                size="sm"
                startIcon={PlusIcon}
                onClick={() => setShowAddHoldingModal(true)}
              >
                Add policy
              </Button>
            ) : undefined
          }
        />
        {showAddHoldingModal && (
          <AddHoldingsFormModal
            open={showAddHoldingModal}
            onClose={() => setShowAddHoldingModal(false)}
            holdingsGroup={undefined}
            defaultHoldingType="insurance"
          />
        )}
      </>
    );
  }

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        pb={2}
      >
        <EstateBreadcrumb
          pageCrumb="Life Insurance"
          householdId={householdId!}
        />
      </Stack>
      <DataTableCard>
        <DataTable
          rows={rows}
          columns={columns}
          loading={loading}
          pageSize="long"
          sortingMode="client"
          sortingOrder={['asc', 'desc']}
          initialState={{
            sorting: {
              sortModel: [{ field: 'deathBenefitAmount', sort: 'desc' }],
            },
          }}
          onRowClick={canEdit ? handleRowClick : undefined}
          noPointer={!canEdit}
          rowHoverVariant={canEdit ? 'default' : 'transparent'}
        />
      </DataTableCard>
      {editingPolicyRow && editDrawerOpen && (
        <EditPolicySideDrawer
          key={editingPolicyRow.id}
          isOpen={editDrawerOpen}
          onClose={handleCloseDrawer}
          entity={editingPolicyRow.entity}
          policy={editingPolicyRow.policy}
        />
      )}
    </Stack>
  );
}
